/**
 * VerifiableRecordList — shared record-review primitive (N4).
 *
 * Owns record-review mechanics ONLY: the base verified/pending badge, rows
 * with a label + injected badge nodes, Verify/Unverify/Reject actions
 * (all injected — this primitive never calls an API), an optional
 * sequential Verify-all with honest partial-failure reporting + retry, and
 * loading / empty / permission states. It absorbs the three duplicated
 * `VerifyAllButton` implementations (ChannelRulesPanel.tsx,
 * VisualIdentityPanel.tsx, BrandBiblePage.tsx §E/§F/§G/§H) into one place.
 *
 * It must NOT infer that a record is scored, public, or contaminated — that
 * is section policy, expressed through `renderBadges` / `renderDetails` /
 * `canVerify` / `verifyAll.disabledReason`.
 *
 * `VerifiableRecord` is the ruled base contract (2026-08-10 handoff):
 * {id, label, verified, verifiedAt?, source?, confidence?, error?}. Sections
 * extend it via the generic `T` — e.g. §F's CompetitorRecord adds `domain`,
 * `position_note`, `our_angle`, `public_mention`.
 */
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface VerifiableRecord {
  id: string;
  label: string;
  verified: boolean;
  verifiedAt?: string;
  source?: string;
  confidence?: 'high' | 'medium' | 'low';
  error?: string;
}

export interface VerifyAllConfig {
  /** false hides the button and renders `disabledReason` instead, if given. */
  enabled: boolean;
  /**
   * Rendered as visible text next to where the button would sit — NEVER a
   * tooltip/title attribute. Used e.g. for "Bulk verify is disabled until
   * curated and discovered records are reconciled" (§F contamination).
   */
  disabledReason?: string;
  /** Appended after the pending count, e.g. "pending" — matches existing copy. */
  suffix?: string;
}

export interface VerifiableRecordListProps<T extends VerifiableRecord> {
  records: T[];
  onVerify: (record: T) => Promise<void> | void;
  onUnverify?: (record: T) => Promise<void> | void;
  onReject?: (record: T) => Promise<void> | void;
  /** false for DIY/non-editor viewers — hides every verify affordance (contract §4.3 precedent). */
  canVerify: boolean;
  renderBadges?: (record: T) => ReactNode;
  renderLabel?: (record: T) => ReactNode;
  renderDetails?: (record: T) => ReactNode;
  loading?: boolean;
  verifyAll?: VerifyAllConfig;
  emptyHint?: ReactNode;
  /**
   * Rendered (visible text, never a tooltip) instead of the list when the
   * viewer has no permission to see records at all. Leave undefined to fall
   * back to normal record-row rendering gated only by `canVerify`.
   */
  permissionDeniedReason?: string;
  getRowKey?: (record: T) => string;
  label?: string;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

/* ------------------------------- VerifyAllControl ------------------------------- */

function VerifyAllControl<T extends VerifiableRecord>({
  pending,
  config,
  onVerify,
}: {
  pending: T[];
  config: VerifyAllConfig;
  onVerify: (record: T) => Promise<void> | void;
}) {
  const [phase, setPhase] = useState<'idle' | 'running' | 'partial'>('idle');
  const [result, setResult] = useState<{ verified: number; failed: number } | null>(null);

  if (!config.enabled) {
    return config.disabledReason ? (
      <p className="text-[11.5px] text-muted-foreground">{config.disabledReason}</p>
    ) : null;
  }
  if (pending.length === 0 && phase !== 'partial') return null;

  const run = async () => {
    setPhase('running');
    let verified = 0;
    let failed = 0;
    for (const record of pending) {
      try {
        // eslint-disable-next-line no-await-in-loop -- sequential, honest partial-failure: no burst against the API.
        await onVerify(record);
        verified += 1;
      } catch {
        failed += 1;
      }
    }
    setResult({ verified, failed });
    setPhase(failed > 0 ? 'partial' : 'idle');
  };

  if (phase === 'partial' && result) {
    return (
      <button
        type="button"
        onClick={() => void run()}
        className="flex-none rounded-md border border-destructive px-2.5 py-1 text-[11.5px] font-semibold text-destructive hover:bg-destructive/10"
      >
        {result.verified} verified · {result.failed} failed — retry
      </button>
    );
  }

  const suffix = config.suffix ?? 'pending';
  return (
    <button
      type="button"
      onClick={() => void run()}
      disabled={phase === 'running'}
      className="flex-none rounded-md border border-coral-dark bg-warm-bg px-2.5 py-1 text-[11.5px] font-semibold text-coral-dark hover:bg-coral-light disabled:cursor-not-allowed disabled:opacity-60"
    >
      {phase === 'running' ? 'Verifying…' : `✓ Verify all · ${pending.length} ${suffix}`}
    </button>
  );
}

/* ---------------------------------- RecordRow ---------------------------------- */

function RecordRow<T extends VerifiableRecord>({
  record,
  canVerify,
  onVerify,
  onUnverify,
  onReject,
  renderBadges,
  renderLabel,
  renderDetails,
}: {
  record: T;
  canVerify: boolean;
  onVerify: (record: T) => Promise<void> | void;
  onUnverify?: (record: T) => Promise<void> | void;
  onReject?: (record: T) => Promise<void> | void;
  renderBadges?: (record: T) => ReactNode;
  renderLabel?: (record: T) => ReactNode;
  renderDetails?: (record: T) => ReactNode;
}) {
  const [busy, setBusy] = useState(false);

  const run = async (action: (record: T) => Promise<void> | void) => {
    setBusy(true);
    try {
      await action(record);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-card px-3 py-2.5" role="listitem">
      <div className="flex items-start gap-2.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-1.5">
            {renderLabel ? renderLabel(record) : <span className="truncate text-[13px] font-medium text-foreground">{record.label}</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {record.verified ? (
              <span className="flex items-center gap-1 text-[11px] text-[hsl(var(--status-success))]">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-success))]" />
                Verified{record.verifiedAt ? ` · ${formatDate(record.verifiedAt)}` : ''}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[11px] text-[hsl(var(--status-warning))]">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-warning))]" />
                Pending verification
              </span>
            )}
            {renderBadges?.(record)}
          </div>
          {record.error && (
            <p role="alert" className="mt-1 text-[11px] text-destructive">
              {record.error}
            </p>
          )}
        </div>
        {canVerify && (
          <div className="flex flex-none items-center gap-2">
            {!record.verified && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(onVerify)}
                className="text-[11.5px] font-semibold text-coral hover:text-coral-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                Verify
              </button>
            )}
            {record.verified && onUnverify && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(onUnverify)}
                className="text-[11.5px] font-medium text-muted-foreground underline decoration-muted-foreground/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
              >
                Unverify
              </button>
            )}
            {onReject && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(onReject)}
                aria-label={`Reject ${record.label}`}
                className="text-[11.5px] font-medium text-destructive hover:text-destructive/80 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reject
              </button>
            )}
          </div>
        )}
      </div>
      {renderDetails?.(record)}
    </div>
  );
}

/* ------------------------------- VerifiableRecordList ------------------------------- */

export function VerifiableRecordList<T extends VerifiableRecord>({
  records,
  onVerify,
  onUnverify,
  onReject,
  canVerify,
  renderBadges,
  renderLabel,
  renderDetails,
  loading = false,
  verifyAll,
  emptyHint,
  permissionDeniedReason,
  getRowKey,
  label = 'Records',
}: VerifiableRecordListProps<T>) {
  if (permissionDeniedReason) {
    return <p className="text-[12px] text-muted-foreground">{permissionDeniedReason}</p>;
  }
  if (loading) {
    return <p className="text-[12px] text-muted-foreground">Loading…</p>;
  }
  if (records.length === 0) {
    return emptyHint ? <div className="text-[12px] text-muted-foreground">{emptyHint}</div> : null;
  }

  const pending = records.filter((r) => !r.verified);

  return (
    <div className="flex flex-col gap-2.5">
      {verifyAll && canVerify && (
        <div className="flex items-center justify-end">
          <VerifyAllControl pending={pending} config={verifyAll} onVerify={onVerify} />
        </div>
      )}
      <div role="list" aria-label={label} className="flex flex-col gap-2">
        {records.map((record) => (
          <RecordRow
            key={getRowKey ? getRowKey(record) : record.id}
            record={record}
            canVerify={canVerify}
            onVerify={onVerify}
            onUnverify={onUnverify}
            onReject={onReject}
            renderBadges={renderBadges}
            renderLabel={renderLabel}
            renderDetails={renderDetails}
          />
        ))}
      </div>
    </div>
  );
}
